import { Info } from 'lucide-react';
import { cn, formatPrice } from '@/lib/utils';

interface PricingBreakdownProps {
  /** Base experience/item amount in cents */
  baseAmount: number;
  /** Service fee amount in cents (null if hotel absorbs) */
  serviceFee: number | null;
  /** Service fee rate as decimal (0.05 = 5%) */
  serviceFeeRate: number | null;
  /** Tax amount in cents */
  taxAmount?: number;
  /** Total amount guest pays in cents */
  totalAmount: number;
  /** Currency code */
  currency: string;
  /** Fee display mode */
  feeDisplayMode: 'guest_pays' | 'hotel_absorbs';
  /** Optional add-ons amount */
  addOnsAmount?: number;
  /** Optional discount amount */
  discountAmount?: number;
  /** Show compact version */
  compact?: boolean;
  /** Custom label for base price */
  baseLabel?: string;
  /** Custom label for service fee */
  serviceFeeLabel?: string;
  /** Show tooltip explaining fee */
  showFeeTooltip?: boolean;
}

/**
 * Reusable pricing breakdown component for widget checkout
 * Handles service fee display based on organization's fee display mode
 */
export function PricingBreakdown({
  baseAmount,
  serviceFee,
  serviceFeeRate,
  taxAmount = 0,
  totalAmount,
  currency,
  feeDisplayMode,
  addOnsAmount = 0,
  discountAmount = 0,
  compact = false,
  baseLabel = 'Experience',
  serviceFeeLabel = 'Service fee',
  showFeeTooltip = true,
}: PricingBreakdownProps) {
  const showServiceFee = feeDisplayMode === 'guest_pays' && serviceFee !== null && serviceFee > 0;
  const hasDiscount = discountAmount > 0;
  const hasAddOns = addOnsAmount > 0;
  const hasTax = taxAmount > 0;

  if (compact) {
    return (
      <div className="space-y-1 text-sm">
        <div className="flex justify-between font-semibold">
          <span>Total</span>
          <span>{formatPrice(totalAmount, currency)}</span>
        </div>
        {showServiceFee && (
          <p className="text-xs text-muted-foreground">
            Includes {Math.round((serviceFeeRate || 0) * 100)}% service fee
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 text-sm">
      {/* Base Price */}
      <div className="flex justify-between">
        <span className="text-muted-foreground">{baseLabel}</span>
        <span>{formatPrice(baseAmount, currency)}</span>
      </div>

      {/* Add-ons */}
      {hasAddOns && (
        <div className="flex justify-between">
          <span className="text-muted-foreground">Add-ons</span>
          <span>{formatPrice(addOnsAmount, currency)}</span>
        </div>
      )}

      {/* Discount */}
      {hasDiscount && (
        <div className="flex justify-between text-green-600">
          <span>Discount</span>
          <span>-{formatPrice(discountAmount, currency)}</span>
        </div>
      )}

      {/* Service Fee (only if guest pays) */}
      {showServiceFee && (
        <div className="flex justify-between text-muted-foreground">
          <span className="flex items-center gap-1">
            {serviceFeeLabel}
            {showFeeTooltip && (
              <span className="group relative cursor-help">
                <Info className="h-3 w-3" />
                <span className="invisible absolute bottom-full left-1/2 mb-2 -translate-x-1/2 rounded bg-foreground px-2 py-1 text-xs text-background opacity-0 transition-all group-hover:visible group-hover:opacity-100 whitespace-nowrap z-10">
                  This fee helps us operate the booking platform
                </span>
              </span>
            )}
          </span>
          <span>{formatPrice(serviceFee, currency)}</span>
        </div>
      )}

      {/* Taxes */}
      {hasTax && (
        <div className="flex justify-between text-muted-foreground">
          <span>Taxes & fees</span>
          <span>{formatPrice(taxAmount, currency)}</span>
        </div>
      )}

      {/* Divider */}
      <div className="border-t my-2" />

      {/* Total */}
      <div className="flex justify-between font-semibold text-base">
        <span>Total</span>
        <span>{formatPrice(totalAmount, currency)}</span>
      </div>
    </div>
  );
}

/**
 * Compact total display with optional fee indicator
 */
interface TotalAmountProps {
  amount: number;
  currency: string;
  serviceFee?: number | null;
  feeDisplayMode?: 'guest_pays' | 'hotel_absorbs';
  className?: string;
}

export function TotalAmount({
  amount,
  currency,
  serviceFee,
  feeDisplayMode,
  className,
}: TotalAmountProps) {
  const showFeeNote = feeDisplayMode === 'guest_pays' && serviceFee && serviceFee > 0;

  return (
    <div className={cn('text-right', className)}>
      <span className="text-2xl font-bold">{formatPrice(amount, currency)}</span>
      {showFeeNote && (
        <p className="text-xs text-muted-foreground mt-1">
          Includes service fee
        </p>
      )}
    </div>
  );
}
